/**
 * Route table — single source of truth for what renders where.
 *
 * Replaces the long chain of `resolvedCurrentPath === '/x' && <PageX />`
 * conditionals in App.js with a declarative configuration. Each entry
 * describes one renderable route:
 *
 *   id                 stable id, used in render conditionals
 *                      (entry.id === 'today' beats path string matching
 *                      because it survives URL renames + alias changes)
 *
 *   path               exact path the entry matches
 *   match(path)        optional matcher for parameterized routes
 *                      (e.g. /employees/profile/:managerId)
 *   shell              which top-level shell hosts the page:
 *                        'director'     director-shell users
 *                        'admin'        admin workspace users
 *                        'ops'          ops manager workspace
 *                        'airport'      airport manager workspace
 *                        'today'        special — focus queue routes
 *                        'starter'      starter mode overlay
 *                        'setup'        location setup overlay
 *                        'any'          rendered regardless of role
 *
 *   requiredPermission optional permission key — same semantics as
 *                      APP_ROUTE_META.requiredPermission
 *   fallbackPermissions  optional fallback permission list
 *
 *   layout             'workspace' | 'starter' | 'setup' | 'raw'
 *                        workspace → standard <WorkspacePage> container
 *                        starter   → starter-mode chrome
 *                        setup     → setup wizard chrome
 *                        raw       → render with no wrapper
 *
 *   opsContextBar      true if the global Operational Context Bar
 *                      should mount above the page
 *
 *   workspaceGroup     used to derive boolean route flags
 *                      (peopleTalent, scorecard, etc.) without each
 *                      page hand-coding a path check
 *
 *   title              human-readable workspace label (mirrors
 *                      APP_ROUTE_META.title — kept here so the route
 *                      table is self-contained)
 *
 * Important: this module does NOT decide whether the user can REACH the
 * route — that's still APP_ROUTE_META + canAccessRoute. The route table
 * decides what RENDERS once the user has navigated. The two layers are
 * consistent because they share the same permission keys.
 */

import {
  APP_ROUTE_META,
  DIRECTOR_SHELL_ROLES,
  WORKSPACE_KEYS,
  getWorkspaceForUser
} from './appRoutes';

const FOCUS_QUEUE_ROLES = new Set(['ops_manager', 'airport_manager', 'director', 'admin']);

/**
 * Pull the title from APP_ROUTE_META if not overridden, so the route
 * table never drifts from the navigation/meta layer.
 */
function metaTitle(path, fallback) {
  return APP_ROUTE_META[path]?.title || fallback || '';
}

function metaPermission(path) {
  return APP_ROUTE_META[path]?.requiredPermission;
}

function metaFallbackPermissions(path) {
  return APP_ROUTE_META[path]?.fallbackPermissions;
}

/**
 * Match an /employees/profile/:managerId-style path.
 */
function matchEmployeeProfilePath(path) {
  return /^\/employees\/profile\/\d+$/.test(String(path || ''));
}

function matchEmployeeProfileByProfilePath(path) {
  return /^\/employees\/profile\/by-profile\/\d+$/.test(String(path || ''));
}

/**
 * W1: match a /workforce/employees/:employeeProfileId canonical path.
 */
function matchWorkforceEmployeePath(path) {
  return /^\/workforce\/employees\/\d+$/.test(String(path || ''));
}

export const ROUTE_TABLE = [
  {
    id: 'timekeeping-admin', path: '/timekeeping/admin', shell: 'any', layout: 'workspace', opsContextBar: false,
    workspaceGroup: 'timekeeping-admin', requiredPermission: metaPermission('/timekeeping/admin'),
    fallbackPermissions: metaFallbackPermissions('/timekeeping/admin'), title: metaTitle('/timekeeping/admin', 'Timekeeping Administration')
  },
  {
    id: 'timekeeping-reports', path: '/timekeeping/reports', shell: 'any', layout: 'workspace', opsContextBar: true,
    workspaceGroup: 'workforce-timekeeping-reports', requiredPermission: metaPermission('/timekeeping/reports'),
    fallbackPermissions: metaFallbackPermissions('/timekeeping/reports'), title: metaTitle('/timekeeping/reports', 'Recorded Hours')
  },
  {
    id: 'attendance', path: '/attendance', shell: 'any', layout: 'workspace', opsContextBar: true,
    workspaceGroup: 'workforce-attendance', requiredPermission: metaPermission('/attendance'),
    fallbackPermissions: metaFallbackPermissions('/attendance'), title: metaTitle('/attendance', 'Attendance')
  },
  {
    id: 'manager-timecards',
    path: '/timecards',
    shell: 'any',
    layout: 'workspace',
    opsContextBar: true,
    workspaceGroup: 'workforce-timecards',
    requiredPermission: metaPermission('/timecards'),
    fallbackPermissions: metaFallbackPermissions('/timecards'),
    title: metaTitle('/timecards', 'Timecards')
  },
  // ─── Today (focus queue, all dashboard-capable roles) ───────────
  {
    id: 'today',
    path: '/today',
    shell: 'today',
    layout: 'workspace',
    opsContextBar: true,
    workspaceGroup: 'today',
    title: metaTitle('/today', 'Today'),
    requiredRoles: FOCUS_QUEUE_ROLES
  },

  // ─── Director leadership shell ──────────────────────────────────
  {
    id: 'executive',
    path: '/executive',
    shell: 'director',
    layout: 'workspace',
    opsContextBar: true,
    workspaceGroup: 'leadership',
    title: metaTitle('/executive', 'Executive Snapshot')
  },
  {
    id: 'alerts',
    path: '/alerts',
    shell: 'director',
    layout: 'workspace',
    opsContextBar: true,
    workspaceGroup: 'alerts',
    title: metaTitle('/alerts', 'Alerts Center')
  },
  {
    id: 'notifications',
    path: '/notifications',
    shell: 'director',
    layout: 'workspace',
    opsContextBar: false,
    workspaceGroup: 'alerts',
    title: metaTitle('/notifications', 'Notifications')
  },
  {
    id: 'goal-plan',
    path: '/goals/plan',
    shell: 'director',
    layout: 'workspace',
    opsContextBar: false,
    workspaceGroup: 'performance',
    title: metaTitle('/goals/plan', 'Goal Plan')
  },

  // ─── People & Talent (one workspace, four URL faces) ────────────
  {
    id: 'people-talent-directory',
    path: '/employees',
    shell: 'director',
    layout: 'workspace',
    opsContextBar: true,
    workspaceGroup: 'people-talent',
    requiredPermission: metaPermission('/employees'),
    fallbackPermissions: metaFallbackPermissions('/employees'),
    title: metaTitle('/employees', 'People & Talent')
  },
  {
    id: 'people-talent-profiles',
    path: '/employees/profiles',
    shell: 'director',
    layout: 'workspace',
    opsContextBar: true,
    workspaceGroup: 'people-talent',
    requiredPermission: metaPermission('/employees/profiles'),
    fallbackPermissions: metaFallbackPermissions('/employees/profiles'),
    title: metaTitle('/employees/profiles', 'People & Talent')
  },
  {
    id: 'people-talent-documents',
    path: '/employees/documents',
    shell: 'director',
    layout: 'workspace',
    opsContextBar: true,
    workspaceGroup: 'people-talent',
    requiredPermission: metaPermission('/employees/documents'),
    fallbackPermissions: metaFallbackPermissions('/employees/documents'),
    title: metaTitle('/employees/documents', 'People & Talent')
  },
  {
    id: 'people-talent-expiring',
    path: '/employees/expiring',
    shell: 'director',
    layout: 'workspace',
    opsContextBar: true,
    workspaceGroup: 'people-talent',
    requiredPermission: metaPermission('/employees/expiring'),
    fallbackPermissions: metaFallbackPermissions('/employees/expiring'),
    title: metaTitle('/employees/expiring', 'People & Talent')
  },
  {
    id: 'people-talent-profile',
    path: '/employees/profile/:managerId',
    match: matchEmployeeProfilePath,
    shell: 'director',
    layout: 'workspace',
    opsContextBar: true,
    workspaceGroup: 'people-talent',
    title: metaTitle('/employees/profile/:managerId', 'People & Talent')
  },
  {
    id: 'people-talent-profile-by-profile',
    path: '/employees/profile/by-profile/:employeeProfileId',
    match: matchEmployeeProfileByProfilePath,
    shell: 'director',
    layout: 'workspace',
    opsContextBar: true,
    workspaceGroup: 'people-talent',
    title: metaTitle('/employees/profile/by-profile/:employeeProfileId', 'People & Talent')
  },

  // ─── Workforce Hub W1 — canonical URLs (alias of the above) ─────
  // Both URL families render EmployeeDirectoryPage. They share the
  // same workspaceGroup so route-guard / context-bar / nav-active
  // logic treats them as a single workspace.
  {
    id: 'workforce-canonical',
    path: '/workforce',
    shell: 'director',
    layout: 'workspace',
    opsContextBar: true,
    workspaceGroup: 'people-talent',
    requiredPermission: metaPermission('/workforce'),
    fallbackPermissions: metaFallbackPermissions('/workforce'),
    title: metaTitle('/workforce', 'Workforce')
  },
  {
    id: 'workforce-canonical-employee',
    path: '/workforce/employees/:employeeProfileId',
    match: matchWorkforceEmployeePath,
    shell: 'director',
    layout: 'workspace',
    opsContextBar: true,
    workspaceGroup: 'people-talent',
    title: metaTitle('/workforce/employees/:employeeProfileId', 'Workforce')
  },

  // ─── Setup / starter / location-setup ───────────────────────────
  {
    id: 'starter-setup',
    path: '/setup',
    shell: 'starter',
    layout: 'setup',
    opsContextBar: false,
    workspaceGroup: 'setup',
    title: metaTitle('/setup', 'Setup Wizard')
  },
  {
    id: 'starter-preview',
    path: '/dashboard-preview',
    shell: 'starter',
    layout: 'setup',
    opsContextBar: false,
    workspaceGroup: 'setup',
    title: metaTitle('/dashboard-preview', 'Dashboard Preview')
  },
  {
    id: 'location-setup',
    path: '/location-setup',
    shell: 'director',
    layout: 'setup',
    opsContextBar: false,
    workspaceGroup: 'setup',
    title: metaTitle('/location-setup', 'Location Setup')
  }
];

/** A map for O(1) path lookup of exact-match entries. */
const ROUTE_TABLE_BY_PATH = new Map(
  ROUTE_TABLE.filter(entry => !entry.match).map(entry => [entry.path, entry])
);

/**
 * Find the route entry for a path. Tries exact path first, then any
 * `match` function entries (for parameterized routes).
 *
 * Returns null when no entry matches. Callers can fall through to the
 * legacy DirectorDashboard / AdminDashboard / OpsManager / Airport
 * shells, which still own the "any-other-path" rendering.
 */
export function findRouteEntry(path) {
  if (!path) return null;
  const exact = ROUTE_TABLE_BY_PATH.get(path);
  if (exact) return exact;
  for (const entry of ROUTE_TABLE) {
    if (entry.match && entry.match(path)) return entry;
  }
  return null;
}

/**
 * Will the given user even render this entry?
 *
 * Mirrors App.js's pre-table conditionals:
 *   - shell 'director'  → DIRECTOR_SHELL_ROLES
 *   - shell 'today'     → FOCUS_QUEUE_ROLES
 *   - shell 'starter'   → user must be in starter mode (caller-checked)
 *   - shell 'setup'     → director-shell with the setup feature
 *   - shell 'ops'       → workspace key 'ops'
 *   - shell 'airport'   → workspace key 'airport'
 *   - shell 'admin'     → workspace key 'admin'
 *   - shell 'any'       → always
 *
 * Permission gating layered on top via requiredPermission /
 * fallbackPermissions (same semantics as APP_ROUTE_META).
 */
export function userCanRenderEntry(entry, user) {
  if (!entry || !user) return false;

  if (entry.requiredRoles && !entry.requiredRoles.has(user?.role)) {
    return false;
  }

  switch (entry.shell) {
    case 'today':
      // FOCUS_QUEUE_ROLES check already done above when requiredRoles
      // is set; for entries without requiredRoles the shell name alone
      // is the gate.
      if (!entry.requiredRoles && !FOCUS_QUEUE_ROLES.has(user?.role)) return false;
      break;
    case 'director':
      if (!DIRECTOR_SHELL_ROLES.has(user?.role)) return false;
      break;
    case 'admin':
      if (getWorkspaceForUser(user) !== WORKSPACE_KEYS.ADMIN) return false;
      break;
    case 'ops':
      if (getWorkspaceForUser(user) !== WORKSPACE_KEYS.OPS) return false;
      break;
    case 'airport':
      if (getWorkspaceForUser(user) !== WORKSPACE_KEYS.AIRPORT) return false;
      break;
    case 'starter':
    case 'setup':
    case 'any':
    default:
      break;
  }

  if (entry.requiredPermission) {
    const perms = Array.isArray(user?.permissions) ? user.permissions : [];
    if (perms.includes(entry.requiredPermission)) return true;
    const fallback = Array.isArray(entry.fallbackPermissions) ? entry.fallbackPermissions : [];
    if (fallback.some(p => perms.includes(p))) return true;
    return false;
  }

  return true;
}

/**
 * Resolve the entry that should render for the given path + user +
 * shell state (starter mode, location setup, etc.). Returns null when
 * the path has no entry or the user lacks access — callers then fall
 * through to the legacy role-shell renderer.
 */
export function resolveRouteEntry(path, user, shellState = {}) {
  const entry = findRouteEntry(path);
  if (!entry) return null;

  // Starter mode overrides everything except the explicit starter entries.
  if (shellState.isStarterMode && entry.shell !== 'starter') {
    // Special case: /location-setup is allowed for the director shell
    // while starter mode is on for that location.
    if (entry.id !== 'location-setup') return null;
  }
  if (!shellState.isStarterMode && entry.shell === 'starter') {
    return null;
  }

  if (!userCanRenderEntry(entry, user)) return null;
  return entry;
}

/**
 * Lookup helper: every workspaceGroup → array of entries. Lets callers
 * (e.g. App.js, OperationalContextBar mounting) ask "is this a
 * people-talent route?" without hand-coded path lists.
 */
export function entriesInWorkspaceGroup(group) {
  return ROUTE_TABLE.filter(entry => entry.workspaceGroup === group);
}

/** Convenience flag for App.js — opsContextBar derived from entry. */
export function shouldShowOpsContextBar(entry, shellState = {}) {
  if (!entry) return false;
  if (shellState.isStarterMode) return false;
  if (shellState.isLocationSetupActive) return false;
  if (shellState.showLocationStarterMode) return false;
  return Boolean(entry.opsContextBar);
}

/** Stable export for tests / App.js conditionals. */
export const ROUTE_IDS = Object.freeze(
  ROUTE_TABLE.reduce((acc, entry) => { acc[entry.id] = entry.id; return acc; }, {})
);
